import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
  Flex,
  Spinner,
  Button,
} from '@chakra-ui/react';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragHandleIcon } from '@chakra-ui/icons';
import isEqual from 'lodash/isEqual';

import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';
import Checkbox from 'components/Checkbox';
import Pencil from 'components/Icons/Pencil';
import {
  getCompareDashboardScenario,
  getEditInstrumentData,
  getCalculationLoadingStatus,
  updateInstrument,
  setEditInstrumentOutput,
  setEditInstrumentData,
  getSelectedDashboardScenario,
} from 'redux/DashboardSliceV2';

import useInstrumentDates from '../../hooks/useInstrumentDates';

import EditTitle from './EditTitle';

interface ColumnSettings {
  actual_column_name: string;
  previous_year: boolean;
  varience_to_py: boolean;
  varience_percentage_py: boolean;
  plan_comparison_column_name: string;
  plan: boolean;
  varience_to_pl: boolean;
  varience_percentage_pl: boolean;
}

interface ColumnSettingsProperties {
  settings: ColumnSettings;
  loading?: boolean;
  // onSettingsChange: (updatedSettings: Partial<ColumnSettings>) => void;
}

const SortableColumn = ({ id, item }: { id: string; item: any }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });
  const style = {
    zIndex: isDragging ? 100 : 'auto',
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab',
  };
  return (
    <Flex
      ref={setNodeRef}
      key={id}
      id={id}
      bgColor={'white'}
      border='1px solid #D9D9D9'
      borderRadius={'8px'}
      paddingY={'8px'}
      paddingX={'12px'}
      mb={'4px'}
      alignItems={'center'}
      justifyContent={'space-between'}
      gap={2}
      title={item?.item}
      style={style}
      {...attributes}
      {...listeners}
    >
      <Text
        fontFamily='Roboto Regular'
        fontWeight='500'
        fontSize={'14px'}
        color='#121826'
        textAlign={'left'}
        textOverflow='ellipsis'
        whiteSpace={'nowrap'}
        overflow='hidden'
      >
        {item?.value}
      </Text>
      <DragHandleIcon
        cursor={'grab'}
        color={'#708090'}
        _focus={{ outline: 'none' }}
        height={'12px'}
        transform='rotate(90deg)'
        flexShrink={0}
      />
    </Flex>
  );
};

const ColumnSettingsPopover = ({ settings, loading = false }: ColumnSettingsProperties) => {
  const isLoading = useSelector(getCalculationLoadingStatus);
  const editInstrumentData = useSelector(getEditInstrumentData);
  const dispatch: AppDispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const columnLabelsMap: Record<string, any> = {
    previous_year: { item: 'Prior Year', value: 'PY' },
    varience_to_py: { item: 'Previous Year Variance', value: '▲PY' },
    varience_percentage_py: { item: 'Previous Year Variance Percentage', value: '▲PY%' },
    varience_to_pl: { item: 'Plan Variance', value: '▲PL' },
    varience_percentage_pl: { item: 'Plan Variance Percentage', value: '▲PL%' },
    // plan: { item: 'Plan (PL)', value: 'PL' },
    plan_comparison_column_name: { item: 'plan_comparison_column_name', value: 'Comparison (PL)' },
    actual_column_name: { item: 'actual_column_name', value: 'Actual' },
  };
  const [localSettings, setLocalSettings] = useState<ColumnSettings>({ ...settings });
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const { buildPreviewPayload } = useInstrumentDates();

  const handleChange = (key: keyof ColumnSettings, value: any) => {
    if (loading) return; // Prevent updates when loading is true
    const updatedSettings: ColumnSettings = { ...localSettings, [key]: value };
    setLocalSettings(updatedSettings);
  };

  const indicators = useMemo(() => {
    return Object.keys(localSettings)
      .filter((key) => {
        if (localSettings[key as keyof ColumnSettings] === true) return true;
        // Actual column is always present — it is not user-toggleable.
        if (key === 'actual_column_name') return true;
        // Only show plan_comparison_column_name if plan, varience_to_pl, or varience_percentage_pl is true
        if (key === 'plan_comparison_column_name') {
          return (
            localSettings.plan ||
            localSettings.varience_to_pl ||
            localSettings.varience_percentage_pl
          );
        }
        return false;
      })
      .map((key) => columnLabelsMap[key as string]);
  }, [localSettings]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [columnOrder, setColumnOrder] = useState<any[]>(() =>
    indicators.filter((item: any) => item?.value).map((item: any) => item.value),
  );

  useEffect(() => {
    setColumnOrder(indicators.filter((item: any) => item?.value).map((item: any) => item.value));
  }, [localSettings]);

  const selectedList = useMemo(() => {
    return (Object.keys(localSettings) as Array<keyof typeof localSettings>)
      .filter((key) => {
        if (localSettings[key as keyof ColumnSettings] === true) return true;
        // Actual column is always present — it is not user-toggleable.
        if (key === 'actual_column_name') return true;
        // Show Prior Year label in the drag list whenever any PY column is enabled
        if (key === 'previous_year') {
          return (
            localSettings.previous_year ||
            localSettings.varience_to_py ||
            localSettings.varience_percentage_py
          );
        }
        // Only show plan_comparison_column_name if plan, varience_to_pl, or varience_percentage_pl is true
        if (key === 'plan_comparison_column_name' || key === 'plan') {
          return (
            localSettings.plan ||
            localSettings.varience_to_pl ||
            localSettings.varience_percentage_pl
          );
        }
        return false;
      })
      .map((key) => columnLabelsMap[key as string])
      .filter(Boolean);
  }, [localSettings]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(active.id);
    const newIndex = columnOrder.indexOf(over.id);
    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
    setColumnOrder(newOrder);

    const columnsPosition: Record<string, number> = {};
    newOrder.forEach((key: string, index: number) => {
      columnsPosition[key] = index + 1;
    });
    const updatedWidget = {
      ...editInstrumentData,
      formatting: {
        ...editInstrumentData?.formatting,
        columns_settings: { ...localSettings },
        columns_position: columnsPosition,
      },
    };
    // Update local state (instrument fields only — no runtime fields like save/data_values).
    dispatch(setEditInstrumentData(updatedWidget));
    // Column-position reorder: no data refetch needed since the backend returns the
    // same values regardless of display order.  data_values:false skips the fetch.
    dispatch(
      updateInstrument({
        request_body: buildPreviewPayload(updatedWidget, {
          scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
          compare_scenario: compareDashboardScenario?.id || null,
          data_values: false,
        }),
      }),
    );
  };

  const getOrderedSelectedList = () => {
    // Use columns_position from localSettings or editInstrumentData
    const columnsPosition: Record<string, number> | undefined =
      editInstrumentData?.formatting?.columns_position;
    if (columnsPosition && Object.keys(columnsPosition).length > 0) {
      // Sort selectedList by columnsPosition
      return selectedList
        .slice()
        .sort((a, b) => (columnsPosition[a.value] || 999) - (columnsPosition[b.value] || 999));
    }
    // Fallback: use columnOrder
    return columnOrder
      .map((value) => selectedList.find((item: any) => item.value === value))
      .filter(Boolean);
  };

  return (
    <Popover
      strategy={'fixed'}
      placement='left'
      direction='ltr'
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Flex
        w='fill-available'
        flexGrow={1}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
      >
        <Text fontFamily='Roboto Bold' fontWeight='600' fontSize='11px' color='#708090'>
          Columns
        </Text>
        <PopoverTrigger>
          <Box px='4' borderRadius='sm' cursor='pointer' onClick={() => setIsOpen(true)}>
            <Pencil color='#708090' />
          </Box>
        </PopoverTrigger>
      </Flex>
      <Box>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
            {getOrderedSelectedList().map((item: any) => (
              <SortableColumn key={item.value} id={item.value} item={item} />
            ))}
          </SortableContext>
        </DndContext>
      </Box>

      <PopoverContent bg='white' p={2} borderRadius='sm' boxShadow='lg' maxW='md'>
        <PopoverArrow />
        <PopoverHeader fontWeight='bold' color='#6562CF' fontSize={'md'}>
          Column Settings
        </PopoverHeader>
        <PopoverBody>
          {loading ? (
            <Flex justify='center' align='center' height='100px'>
              <Spinner size='lg' color='purple.500' />
            </Flex>
          ) : (
            <Stack spacing={3}>
              {/* Primary Column Name */}
              <Box>
                <Text color={'black'} mb={3} fontSize='sm'>
                  Primary column name
                </Text>
                <EditTitle
                  showLabel={false}
                  title={localSettings.actual_column_name}
                  setTitle={(event: any) => handleChange('actual_column_name', event)}
                  placeholder='Leave empty to use AC / FC'
                />
              </Box>

              {/* Previous Year Section */}
              <Box>
                <Text color={'black'} mb={3} fontSize='sm'>
                  Show Previous Year (PY)
                </Text>
                <Stack spacing={3}>
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.previous_year}
                    onChange={(event: any) => handleChange('previous_year', event.target.checked)}
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Previous Year (PY)
                      </Text>
                    }
                  />
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.varience_to_py}
                    onChange={(event: any) => handleChange('varience_to_py', event.target.checked)}
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Variance to PY
                      </Text>
                    }
                  />
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.varience_percentage_py}
                    onChange={(event: any) =>
                      handleChange('varience_percentage_py', event.target.checked)
                    }
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Variance % to PY
                      </Text>
                    }
                  />
                </Stack>
              </Box>

              <Box>
                <Text color={'black'} mb={3} fontSize='sm'>
                  Show Plan (PL) Comparison
                </Text>

                <Box mb={5} w='full'>
                  <EditTitle
                    showLabel={false}
                    title={localSettings.plan_comparison_column_name}
                    setTitle={(event: any) => handleChange('plan_comparison_column_name', event)}
                    placeholder='Custom column name'
                  />
                </Box>
                <Stack spacing={3} mt={3}>
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.plan}
                    onChange={(event: any) => handleChange('plan', event.target.checked)}
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Plan (PL)
                      </Text>
                    }
                  />
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.varience_to_pl}
                    onChange={(event: any) => handleChange('varience_to_pl', event.target.checked)}
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Variance to PL
                      </Text>
                    }
                  />
                  <Checkbox
                    size='lg'
                    isChecked={localSettings.varience_percentage_pl}
                    onChange={(event: any) =>
                      handleChange('varience_percentage_pl', event.target.checked)
                    }
                    label={
                      <Text
                        variant='secondary'
                        fontFamily='regular_roboto'
                        fontSize='xs'
                        color={'black'}
                      >
                        Variance % to PL
                      </Text>
                    }
                  />
                </Stack>
                <Flex justifyContent='flex-end' alignItems='center' p={2} gap={2}>
                  <Button variant={'cancel_button'} onClick={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant={'save_button'}
                    onClick={() => {
                      setTimeout(() => {
                        if (
                          isEqual(localSettings, editInstrumentData?.formatting?.columns_settings)
                        )
                          return;
                        // Save the new order in localSettings (add a new property columns_position)
                        const columnsPosition: Record<string, number> = {};
                        columnOrder.forEach((key: string, index: number) => {
                          columnsPosition[key] = index + 1;
                        });
                        const newSettings = {
                          ...localSettings,
                        };
                        const updatedWidget = {
                          ...editInstrumentData,
                          formatting: {
                            ...editInstrumentData?.formatting,
                            columns_settings: newSettings,
                            columns_position: columnsPosition,
                          },
                        };
                        // Update local state with instrument fields only.
                        dispatch(setEditInstrumentData(updatedWidget));
                        // Column-settings change may affect which variance columns the
                        // backend includes (e.g. toggling Plan on requires a comparison
                        // scenario fetch), so data_values:true is correct here.
                        dispatch(
                          updateInstrument({
                            request_body: buildPreviewPayload(updatedWidget, {
                              scenario_id: selectedDashboardScenario?.id
                                ? `${selectedDashboardScenario?.id}`
                                : '',
                              compare_scenario: compareDashboardScenario?.id || null,
                            }),
                          }),
                        ).then((result) => {
                          dispatch(setEditInstrumentOutput(result?.payload?.data));
                          setIsOpen(false);
                        });
                      }, 1000);
                    }}
                    disabled={
                      !(isLoading === STATUS.SUCCESS || isLoading === STATUS.IDLE) ||
                      !editInstrumentData ||
                      !editInstrumentData?.instrument_indicators?.length
                    }
                  >
                    Apply
                  </Button>
                </Flex>
              </Box>
            </Stack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnSettingsPopover;
